import type { AdminUsageSnapshot } from "./types";

export interface AdminUsageDraft {
  engine: AdminUsageSnapshot["tts_daily"]["engine"];
  imageGlobal: string;
  imageUser: string;
  audioGlobal: string;
  audioUser: string;
}

export interface AdminUsageSettingsValue {
  engine: AdminUsageSnapshot["tts_daily"]["engine"];
  image_global_daily_limit: number;
  image_user_daily_limit: number | null;
  audio_global_daily_limit_ms: number | null;
  audio_user_daily_limit_ms: number | null;
}

interface ParsedDraft {
  value?: AdminUsageSettingsValue;
  error?: string;
}

function minutesDraft(value: number | null): string {
  return value === null ? "" : String(value / 60_000);
}

export function adminUsageDraftFromSnapshot(snapshot: AdminUsageSnapshot): AdminUsageDraft {
  return {
    engine: snapshot.tts_daily.engine,
    imageGlobal: String(snapshot.image_daily.limit),
    imageUser: snapshot.image_daily.user_limit === null ? "" : String(snapshot.image_daily.user_limit),
    audioGlobal: minutesDraft(snapshot.tts_daily.global_limit_ms),
    audioUser: minutesDraft(snapshot.tts_daily.user_limit_ms),
  };
}

function parseRequiredImageLimit(value: string, max: number): number | undefined {
  if (value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= max ? parsed : undefined;
}

function parseOptionalImageLimit(value: string, max: number): number | null | undefined {
  if (value.trim() === "") return null;
  return parseRequiredImageLimit(value, max);
}

function parseMinutes(value: string): number | null | undefined {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  const milliseconds = parsed * 60_000;
  return Number.isFinite(parsed) && parsed >= 0 && Number.isSafeInteger(milliseconds)
    ? milliseconds
    : undefined;
}

export function parseAdminUsageDraft(draft: AdminUsageDraft, maxImageLimit: number): ParsedDraft {
  const imageGlobal = parseRequiredImageLimit(draft.imageGlobal, maxImageLimit);
  const imageUser = parseOptionalImageLimit(draft.imageUser, maxImageLimit);
  if (imageGlobal === undefined || imageUser === undefined) {
    return { error: `CivitAI 配额必须是 0 到 ${maxImageLimit} 的整数；全局配额不能为空，用户配额留空表示不限。` };
  }

  const audioGlobal = parseMinutes(draft.audioGlobal);
  const audioUser = parseMinutes(draft.audioUser);
  if (audioGlobal === undefined || audioUser === undefined) {
    return { error: "TTS 配额必须是非负分钟数，留空表示不限。" };
  }

  return {
    value: {
      engine: draft.engine,
      image_global_daily_limit: imageGlobal,
      image_user_daily_limit: imageUser,
      audio_global_daily_limit_ms: audioGlobal,
      audio_user_daily_limit_ms: audioUser,
    },
  };
}

export function isAdminUsageDraftDirty(snapshot: AdminUsageSnapshot, draft: AdminUsageDraft): boolean {
  const parsed = parseAdminUsageDraft(draft, snapshot.image_daily.max_limit);
  if (!parsed.value) return true;
  return parsed.value.engine !== snapshot.tts_daily.engine
    || parsed.value.image_global_daily_limit !== snapshot.image_daily.limit
    || parsed.value.image_user_daily_limit !== snapshot.image_daily.user_limit
    || parsed.value.audio_global_daily_limit_ms !== snapshot.tts_daily.global_limit_ms
    || parsed.value.audio_user_daily_limit_ms !== snapshot.tts_daily.user_limit_ms;
}
